<script>
import Title from "@/components/admin/common/TitleArea.vue"
import StatusBadge from "@/components/fulfillment/StatusBadge.vue"
import DataTable from "@/components/fulfillment/DataTable.vue"
import PageGuideDialog from "@/components/admin/fulfillment/guide/PageGuideDialog.vue"
import FulfillmentPageGuide from "@/components/admin/fulfillment/guide/FulfillmentPageGuide.vue"

export default {
  name: 'AdminFulfillmentOpsDashboard',
  components: {
    Title,
    StatusBadge,
    DataTable,
    PageGuideDialog,
    FulfillmentPageGuide
  },
  data() {
    return {
      description: {
        title: '상품 운영 현황'
      },
      loading: false,
      guideDialog: false,
      summary: {
        approvalPending: 0,
        mappingPending: 0,
        mappedCount: 0,
        unmappedCount: 0,
        todayRequested: 0,
        recentRejected: 0,
      },
      approvalRows: [],
      mappingRows: [],
      oldestPendingRows: [],
      recentRejectedRows: [],
    }
  },
  methods: {
    applyRouteContext() {
      const routeTitle = String(this.$route?.meta?.title || '').replace(/^Aone 관리자 \|\s*/, '')
      if (routeTitle) {
        this.description.title = routeTitle
      }
    },
    async fetchTotal(url) {
      const config = {
        method: 'GET',
        url,
        name: '[ Fulfillment / 운영 현황 요약 ]'
      }
      const res = await this.$http(config)
      return Number(res?.data?.data?.total || 0)
    },
    normalizeRows(data) {
      if (Array.isArray(data)) return data
      if (Array.isArray(data?.items)) return data.items
      if (Array.isArray(data?.list)) return data.list
      return []
    },
    async loadDashboard() {
      this.loading = true
      try {
        const [
          approvalSummaryRes,
          mappingPending,
          mappedCount,
          unmappedCount,
          approvalListRes,
          mappingListRes,
        ] = await Promise.all([
          this.$http({
            method: 'GET',
            url: '/api/fulfillment/admin/sku-requests/ops-summary',
            name: '[ Fulfillment / 운영 현황 승인 요약 ]'
          }),
          this.fetchTotal('/api/fulfillment/admin/master-skus/mappings?status=PENDING&page=1&pageSize=1'),
          this.fetchTotal('/api/fulfillment/admin/master-skus?mapping_status=MAPPED&page=1&pageSize=1'),
          this.fetchTotal('/api/fulfillment/admin/master-skus?mapping_status=UNMAPPED&page=1&pageSize=1'),
          this.$http({
            method: 'GET',
            url: '/api/fulfillment/admin/sku-requests?status=REQUESTED&page=1&pageSize=5',
            name: '[ Fulfillment / 운영 현황 승인 대기 ]'
          }),
          this.$http({
            method: 'GET',
            url: '/api/fulfillment/admin/master-skus/mappings?status=PENDING&page=1&pageSize=5',
            name: '[ Fulfillment / 운영 현황 연결 처리 ]'
          }),
        ])

        const approvalSummary = approvalSummaryRes?.data?.data || {}

        this.summary = {
          approvalPending: Number(approvalSummary.approval_pending || 0),
          mappingPending,
          mappedCount,
          unmappedCount,
          todayRequested: Number(approvalSummary.today_requested || 0),
          recentRejected: Number(approvalSummary.recent_rejected || 0),
        }
        this.approvalRows = this.normalizeRows(approvalListRes?.data?.data)
        this.mappingRows = this.normalizeRows(mappingListRes?.data?.data)
        this.oldestPendingRows = approvalSummary.oldest_pending || []
        this.recentRejectedRows = approvalSummary.recent_rejected_items || []
      } catch (_) {
        this.summary = {
          approvalPending: 0,
          mappingPending: 0,
          mappedCount: 0,
          unmappedCount: 0,
          todayRequested: 0,
          recentRejected: 0,
        }
        this.approvalRows = []
        this.mappingRows = []
        this.oldestPendingRows = []
        this.recentRejectedRows = []
      } finally {
        this.loading = false
      }
    },
    goToApprovals() {
      this.$router.push('/admin/fulfillment/approvals')
    },
    goToOldestApprovals() {
      this.$router.push('/admin/fulfillment/approvals?sort=oldest')
    },
    goToRejected() {
      this.$router.push('/admin/fulfillment/approvals?status=REJECTED')
    },
    goToMappings() {
      this.$router.push('/admin/fulfillment/mappings')
    },
    goToProducts(mappingStatus = '') {
      this.$router.push({
        path: '/admin/fulfillment/master-skus',
        query: mappingStatus ? { mapping_status: mappingStatus } : {}
      })
    },
    openApproval(row) {
      if (!row?.request_id) return
      this.$router.push(`/admin/fulfillment/approvals/${row.request_id}`)
    },
    openMapping(row) {
      if (row?.request_id) {
        this.$router.push(`/admin/fulfillment/approvals/${row.request_id}`)
        return
      }
      if (!row?.master_sku_id) return
      this.$router.push(`/admin/fulfillment/master-skus/${row.master_sku_id}`)
    },
    formatDateTime(value) {
      return value ? this.$formatDate(value, 'YYYY-MM-DD HH:mm:ss') : '-'
    }
  },
  mounted() {
    this.applyRouteContext()
    this.loadDashboard()
  }
}
</script>

<template>
  <q-page class="q-pa-lg">
    <div class="row q-mb-lg">
      <div class="col">
        <Title :desc="description">
          <template #title-action>
            <q-btn flat round dense size="sm" color="primary" icon="help_outline" aria-label="상품 운영 현황 사용 가이드" @click="guideDialog = true">
              <q-tooltip>상품 운영 현황 사용 가이드</q-tooltip>
            </q-btn>
          </template>
          <q-btn flat round dense color="primary" icon="refresh" aria-label="새로고침" @click="loadDashboard">
            <q-tooltip>새로고침</q-tooltip>
          </q-btn>
        </Title>
      </div>
    </div>

    <div class="row q-col-gutter-md q-mb-md items-stretch">
      <div class="col-12 col-md-3">
        <div class="comp-box q-pa-md dashboard-card">
          <div class="row items-start justify-between no-wrap">
            <div class="text-caption text-grey">승인 대기</div>
            <q-btn flat round dense color="primary" icon="open_in_new" aria-label="요청 대기 보기" @click="goToApprovals">
              <q-tooltip>요청 대기 보기</q-tooltip>
            </q-btn>
          </div>
          <div class="text-h5 q-mt-sm">{{ summary.approvalPending }}</div>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="comp-box q-pa-md dashboard-card">
          <div class="row items-start justify-between no-wrap">
            <div class="text-caption text-grey">연결 검토 필요</div>
            <q-btn flat round dense color="primary" icon="open_in_new" aria-label="연결 검토 보기" @click="goToMappings">
              <q-tooltip>연결 검토 보기</q-tooltip>
            </q-btn>
          </div>
          <div class="text-h5 q-mt-sm">{{ summary.mappingPending }}</div>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="comp-box q-pa-md dashboard-card">
          <div class="row items-start justify-between no-wrap">
            <div class="text-caption text-grey">정상 연결 상품</div>
            <q-btn flat round dense color="primary" icon="open_in_new" aria-label="정상 연결 상품 보기" @click="goToProducts('MAPPED')">
              <q-tooltip>정상 연결 상품 보기</q-tooltip>
            </q-btn>
          </div>
          <div class="text-h5 q-mt-sm">{{ summary.mappedCount }}</div>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="comp-box q-pa-md dashboard-card">
          <div class="row items-start justify-between no-wrap">
            <div class="text-caption text-grey">미연결 상품</div>
            <q-btn flat round dense color="primary" icon="open_in_new" aria-label="미연결 상품 보기" @click="goToProducts('UNMAPPED')">
              <q-tooltip>미연결 상품 보기</q-tooltip>
            </q-btn>
          </div>
          <div class="text-h5 q-mt-sm">{{ summary.unmappedCount }}</div>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="comp-box q-pa-md dashboard-card">
          <div class="row items-start justify-between no-wrap">
            <div class="text-caption text-grey">오늘 신규 요청</div>
            <q-btn flat round dense color="primary" icon="open_in_new" aria-label="요청 대기 보기" @click="goToApprovals">
              <q-tooltip>요청 대기 보기</q-tooltip>
            </q-btn>
          </div>
          <div class="text-h5 q-mt-sm">{{ summary.todayRequested }}</div>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="comp-box q-pa-md dashboard-card">
          <div class="row items-start justify-between no-wrap">
            <div class="text-caption text-grey">최근 7일 거절</div>
            <q-btn flat round dense color="primary" icon="open_in_new" aria-label="반려 요청 보기" @click="goToRejected">
              <q-tooltip>반려 요청 보기</q-tooltip>
            </q-btn>
          </div>
          <div class="text-h5 q-mt-sm">{{ summary.recentRejected }}</div>
        </div>
      </div>
    </div>

    <div class="row q-col-gutter-md items-stretch">
      <div class="col-12 col-lg-6">
        <div class="comp-box dashboard-panel">
          <div class="row items-center justify-between q-pa-md">
            <div class="text-subtitle1">전체 요청 대기 목록</div>
            <q-btn flat round dense color="primary" icon="open_in_new" aria-label="전체 요청 대기 보기" @click="goToApprovals">
              <q-tooltip>전체 요청 대기 보기</q-tooltip>
            </q-btn>
          </div>
          <DataTable
            :rows="approvalRows"
            :columns="[
              { name: 'partner_name', label: '파트너', field: row => row.partner_name || `#${row.partner_id}`, align: 'left' },
              { name: 'partner_sku_code', label: '파트너 SKU', field: 'partner_sku_code', align: 'left' },
              { name: 'barcode', label: '바코드', field: row => row.barcode || '-', align: 'left' },
              { name: 'brand_category', label: '브랜드/카테고리', field: row => `${row.brand_name || ''}/${row.category_name || ''}`, align: 'left' },
              { name: 'status', label: '상태', field: 'status', align: 'center' },
            ]"
            row-key="request_id"
            :loading="loading"
            :pagination="{ rowsPerPage: 5 }"
            @row-click="openApproval"
          >
            <template #body-cell-status="props">
              <q-td :props="props">
                <StatusBadge :status="props.value" />
              </q-td>
            </template>
          </DataTable>
        </div>
      </div>

      <div class="col-12 col-lg-6">
        <div class="comp-box dashboard-panel">
          <div class="row items-center justify-between q-pa-md">
            <div class="text-subtitle1">연결 검토 목록</div>
            <q-btn flat round dense color="primary" icon="open_in_new" aria-label="전체 연결 검토 보기" @click="goToMappings">
              <q-tooltip>전체 연결 검토 보기</q-tooltip>
            </q-btn>
          </div>
          <DataTable
            :rows="mappingRows"
            :columns="[
              { name: 'partner_name', label: '파트너', field: row => row.partner_name || `#${row.partner_id}`, align: 'left' },
              { name: 'partner_sku_code', label: '파트너 SKU', field: 'partner_sku_code', align: 'left' },
              { name: 'barcode', label: '바코드', field: row => row.barcode || '-', align: 'left' },
              { name: 'display_name', label: '대상 상품', field: 'display_name', align: 'left' },
              { name: 'mapping_status', label: '상태', field: 'mapping_status', align: 'center' },
            ]"
            row-key="request_id"
            :loading="loading"
            :pagination="{ rowsPerPage: 5 }"
            @row-click="openMapping"
          >
            <template #body-cell-mapping_status="props">
              <q-td :props="props">
                <StatusBadge :status="props.value" />
              </q-td>
            </template>
          </DataTable>
        </div>
      </div>
    </div>

    <div class="row q-col-gutter-md q-mt-md items-stretch">
      <div class="col-12 col-lg-6">
        <div class="comp-box dashboard-panel">
          <div class="row items-center justify-between q-pa-md">
            <div class="text-subtitle1">장기 미처리 요청</div>
            <q-btn flat round dense color="primary" icon="open_in_new" aria-label="오래된 요청 보기" @click="goToOldestApprovals">
              <q-tooltip>오래된 요청 보기</q-tooltip>
            </q-btn>
          </div>
          <DataTable
            :rows="oldestPendingRows"
            :columns="[
              { name: 'aging_days', label: '경과일', field: row => `${row.aging_days || 0}일`, align: 'center' },
              { name: 'partner_name', label: '파트너', field: row => row.partner_name || `#${row.partner_id}`, align: 'left' },
              { name: 'partner_sku_code', label: '파트너 SKU', field: 'partner_sku_code', align: 'left' },
              { name: 'barcode', label: '바코드', field: row => row.barcode || '-', align: 'left' },
              { name: 'brand_category', label: '브랜드/카테고리', field: row => `${row.brand_name || ''}/${row.category_name || ''}`, align: 'left' },
            ]"
            row-key="request_id"
            :loading="loading"
            :pagination="{ rowsPerPage: 5 }"
            @row-click="openApproval"
          />
        </div>
      </div>

      <div class="col-12 col-lg-6">
        <div class="comp-box dashboard-panel">
          <div class="row items-center justify-between q-pa-md">
            <div class="text-subtitle1">최근 거절</div>
            <q-btn flat round dense color="primary" icon="open_in_new" aria-label="반려 요청 보기" @click="goToRejected">
              <q-tooltip>반려 요청 보기</q-tooltip>
            </q-btn>
          </div>
          <DataTable
            :rows="recentRejectedRows"
            :columns="[
              { name: 'partner_name', label: '파트너', field: row => row.partner_name || `#${row.partner_id}`, align: 'left' },
              { name: 'partner_sku_code', label: '파트너 SKU', field: 'partner_sku_code', align: 'left' },
              { name: 'barcode', label: '바코드', field: row => row.barcode || '-', align: 'left' },
              { name: 'reject_reason', label: '거절 사유', field: row => row.reject_reason || '-', align: 'left' },
              { name: 'processed_at', label: '처리 시각', field: row => formatDateTime(row.processed_at), align: 'left' },
            ]"
            row-key="request_id"
            :loading="loading"
            :pagination="{ rowsPerPage: 5 }"
            @row-click="openApproval"
          />
        </div>
      </div>
    </div>

    <PageGuideDialog
      v-model="guideDialog"
      title="상품 운영 현황 사용 가이드"
      description="상품 관리 업무 현황과 우선 처리 항목을 확인하는 방법입니다."
    >
      <FulfillmentPageGuide guide-key="operations" />
    </PageGuideDialog>
  </q-page>
</template>

<style scoped lang="scss">
.dashboard-card,
.dashboard-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
